import { Button } from '@progress/kendo-react-buttons';
import { DateRangePicker, SelectionRange } from '@progress/kendo-react-dateinputs';
import { Dialog } from '@progress/kendo-react-dialogs';
import { InputSuffix, TextBox } from '@progress/kendo-react-inputs';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { hideLoading, showLoading } from '../../actions/api';
import { showNotification } from '../../actions/notifications';
import { SharedLink, StorageItem, StorageKind } from '../../constants/document';
import NotificationsTypes from '../../constants/notification';
import documentsService from '../../services/documentsService';
import './SharedLink.css';

interface ISharedLinkProps {
  item: StorageItem;
  onClose?(): void;
}

const SharedLinkCreator = ({ item, onClose }: ISharedLinkProps): React.ReactElement => {
  const dispatch = useDispatch();
  const [sharedLink, setSharedLink] = useState<SharedLink>();
  const [valueDate, setValueDate] = useState<SelectionRange>();

  useEffect(() => {
    const end = new Date();
    end.setMonth(end.getMonth() + 1);
    end.setDate(end.getDate() + 1);
    setValueDate({
      start: formatGetOnlyDate(new Date()),
      end: formatGetOnlyDate(end),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildSharedUrl = (codeLink: string) =>
    `${window.location.origin}/shared/${item.kind === StorageKind.document ? 'file' : 'folder'}/${codeLink}`;

  const createSharedLink = async (item: StorageItem) => {
    try {
      dispatch(showLoading());
      const result = await documentsService.createSharedLink(
        item.path,
        formatGetOnlyDate(valueDate?.start ?? new Date()),
        formatGetOnlyDate(valueDate?.end ?? new Date()),
      );
      setSharedLink(result);
      // Build the URL from the fresh `result.codeLink` instead of the
      // stale `sharedLink` state (setState is async — the closure here
      // still sees the previous value, which on the very first call is
      // `undefined` and produces a malformed `.../shared/file/undefined`).
      await copySharedLinkToClipboard(buildSharedUrl(result.codeLink));
    } catch (error: any) {
      dispatch(
        showNotification({
          message: 'Create shared link failed, try again later or contact the support team',
          type: NotificationsTypes.Error,
        }),
      );
    } finally {
      dispatch(hideLoading());
    }
  };

  const formatGetOnlyDate = (value: Date) =>
    new Date(value.getFullYear(), value.getMonth(), value.getDate(), 0, 0, 0, 0);

  const formatDisplayDate = (value: Date | null | undefined): string => {
    if (!value) return '';
    const day = String(value.getDate()).padStart(2, '0');
    const month = String(value.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${value.getFullYear()}`;
  };

  const copySharedLinkToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      dispatch(
        showNotification({
          message: 'Link copied to clipboard',
          type: NotificationsTypes.Success,
        }),
      );
    } catch {
      dispatch(
        showNotification({
          message: 'Could not copy link to clipboard',
          type: NotificationsTypes.Error,
        }),
      );
    }
  };

  const url = sharedLink?.codeLink ? buildSharedUrl(sharedLink.codeLink) : '';
  
  return (
    <Dialog
      title={
        <span className="shared-link-title">
          <span className="k-icon k-i-share shared-link-title-icon" />
          Share {item.kind === StorageKind.document ? 'file' : 'folder'} — {item.text}
        </span>
      }
      onClose={onClose}
      width={560}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          createSharedLink(item);
        }}
      >
        <div>
          <p>Select the dates where the link is valid.</p>
          <DateRangePicker
            min={formatGetOnlyDate(new Date())}
            value={valueDate}
            onChange={(event: any) => setValueDate(event.value)}
            format="dd/MM/yyyy"
            popupSettings={{ style: { zIndex: 10005 } }}
          />
        </div>
        {!sharedLink?.codeLink && (
          <Button type="submit" className="btn-action-add">
            Get Link
          </Button>
        )}
      </form>
      {sharedLink?.codeLink && (
        <div className="shared-link-result">
          <label className="shared-link-result-label" htmlFor="shared-link-url">
            Your shared link
          </label>
          <TextBox
            id="shared-link-url"
            className="shared-link-result-input"
            value={url}
            readOnly
            suffix={() => (
              <InputSuffix>
                <Button
                  icon="copy"
                  fillMode="flat"
                  title="Copy link"
                  onClick={() => copySharedLinkToClipboard(url)}
                />
              </InputSuffix>
            )}
          />
          <p className="shared-link-result-hint">
            <span className="k-icon k-i-info shared-link-result-hint-icon" />
            Anyone with this link can access the {item.kind === StorageKind.document ? 'file' : 'folder'} until{' '}
            <strong>{formatDisplayDate(valueDate?.end)}</strong>.
          </p>
        </div>
      )}
    </Dialog>
  );
};

export default SharedLinkCreator;
